import { useEffect } from 'react';

import { useSignalRUpdates } from '@/components/providers/SignalRProvider';
import { useCommunity } from '@/hooks/useCommunity';
import { useIsLargeScreen } from '@/hooks/useIsLargeScreen';

import { useOptionalScreenOptions } from './ScreenOptionsContext';

interface IWebDocumentTitleProps {
  /** Explicit screen title for routes that do not use ScreenOptionsProvider. */
  title?: string;
  /** Optional app/community name override. Defaults to current community or Grokio. */
  appName?: string;
  /** Adds the unread notification prefix on large-screen web app routes. */
  includeNotificationCount?: boolean;
}

/**
 * Keeps the browser tab title in sync with either the active app-shell screen or an explicit title.
 *
 * Title format: `[optional notification prefix][community name] - [screen title]`.
 * On large viewports, when there are unread notifications, a `"(n) "` prefix is prepended
 * only when `includeNotificationCount` is true.
 *
 * When `title` is omitted, this reads `ScreenOptionsProvider` if present; this is how the
 * authenticated app shell maps `ScreenHeader` options to `document.title`. Routes outside
 * that shell can pass `title` directly and avoid any notification-count dependency.
 *
 * @returns Renders nothing; side effect is `document.title` only.
 */
export function WebDocumentTitle({
  title,
  appName: appNameOverride,
  includeNotificationCount = false,
}: IWebDocumentTitleProps) {
  return includeNotificationCount ? (
    <WebDocumentTitleWithNotifications title={title} appName={appNameOverride} />
  ) : (
    <WebDocumentTitleBase title={title} appName={appNameOverride} notificationCount={0} />
  );
}

function WebDocumentTitleWithNotifications({ title, appName }: IWebDocumentTitleProps) {
  const { notificationCount } = useSignalRUpdates();

  return (
    <WebDocumentTitleBase title={title} appName={appName} notificationCount={notificationCount} />
  );
}

function WebDocumentTitleBase({
  title,
  appName: appNameOverride,
  notificationCount,
}: IWebDocumentTitleProps & { notificationCount: number }) {
  const screenOptions = useOptionalScreenOptions();
  const community = useCommunity();
  const isLargeScreen = useIsLargeScreen();

  useEffect(() => {
    const screenTitle =
      title ??
      (typeof screenOptions?.currentOptions.headerTitle === 'string'
        ? screenOptions.currentOptions.headerTitle
        : undefined) ??
      screenOptions?.currentOptions.title;

    const appName = appNameOverride || community?.name || 'Grokio';
    const notificationPrefix =
      isLargeScreen && notificationCount > 0 ? `(${notificationCount}) ` : '';
    const baseTitle = screenTitle ? `${appName} - ${screenTitle}` : appName;

    document.title = `${notificationPrefix}${baseTitle}`;
  }, [
    title,
    screenOptions?.currentOptions,
    appNameOverride,
    community,
    isLargeScreen,
    notificationCount,
  ]);

  return null;
}
